// Undo / redo — pure diff & patch helpers. No Angular here; UndoHistoryService wires them to
// the state signals, tools/test-undo-history.mjs tests this file. See src/CLAUDE.md «Undo / redo».
//
// A step is stored as a keyed, field-level diff and applied as an inverse onto the CURRENT
// highlights — never as a whole-array restore: live-poll / stream recalcs / clip + marker
// refetches / AI results rewrite highlights[] between user steps, and an old snapshot would
// drop or resurrect their data. Highlight objects are immutable (every writer spreads), so
// diffs keep object references, not copies.
import type { ChatFilters, Highlight } from '../models/twitch.model';
import type { TimeRegion } from './region';
import { userClipKey } from './user-clips.js';
import { uniqueStartTime } from './transcript.js';
import { overlapsRegion } from './region.js';

export type HlKey = string;
export type Dir = 'undo' | 'redo';

/** Marks a field the object didn't have — applying it deletes the key instead of writing undefined. */
export const ABSENT: unique symbol = Symbol('absent');

// --- Identity ---

/**
 * Identity of a highlight across rewrites. Highlights are looked up by startTime app-wide, but
 * startTime collides across sources (a peak and an ai-deep-dive on the same 15s bin), user clips
 * keep their slug while mergeUserClips moves their startTime, and stream markers are carried by
 * source + time in reapplyStreamMarkers.
 */
export function baseKey(h: Highlight): HlKey {
  if (h.source === 'user-clip') return userClipKey(h);
  if (h.source === 'stream-marker') return `m:${h.markerSourceId ?? ''}:${h.startTime}`;
  return `${h.source ?? 'peak'}|${h.startTime}`;
}

/** Keys of a list in order; the n-th repeat of a key gets `#n`. */
export function keysOf(list: readonly Highlight[]): HlKey[] {
  const seen = new Map<string, number>();
  return list.map(h => {
    const base = baseKey(h);
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return n === 0 ? base : `${base}#${n}`;
  });
}

function indexByKey(list: readonly Highlight[]): Map<HlKey, number> {
  const m = new Map<HlKey, number>();
  keysOf(list).forEach((k, i) => m.set(k, i));
  return m;
}

export function keyAt(list: readonly Highlight[], index: number): HlKey | null {
  if (index < 0 || index >= list.length) return null;
  return keysOf(list)[index];
}

export function indexOfKey(list: readonly Highlight[], key: HlKey | null): number {
  if (key == null) return -1;
  return indexByKey(list).get(key) ?? -1;
}

/** Key of this exact object (by reference) in the list. */
export function keyOfObject(list: readonly Highlight[], h: Highlight | null | undefined): HlKey | null {
  if (!h) return null;
  const i = list.indexOf(h);
  return i === -1 ? null : keysOf(list)[i];
}

// --- Highlight diff ---

export type FieldChanges = Record<string, [unknown, unknown]>;
export interface HlChange { key: HlKey; fields: FieldChanges; before: Highlight; after: Highlight }
export interface HlDiff {
  removed: Array<{ key: HlKey; h: Highlight }>; // only in BEFORE (the before-object)
  added: Array<{ key: HlKey; h: Highlight }>;   // only in AFTER (the after-object)
  changed: HlChange[];
}

const own = (o: object, f: string) => Object.prototype.hasOwnProperty.call(o, f);

function fieldDiff(b: Highlight, a: Highlight): FieldChanges | null {
  let out: FieldChanges | null = null;
  const fields = new Set([...Object.keys(b), ...Object.keys(a)]);
  for (const f of fields) {
    const bv = own(b, f) ? (b as any)[f] : ABSENT;
    const av = own(a, f) ? (a as any)[f] : ABSENT;
    if (!Object.is(bv, av)) (out ??= {})[f] = [bv, av];
  }
  return out;
}

function withFields(h: Highlight, fields: FieldChanges, side: 0 | 1): Highlight {
  const out: any = { ...h };
  for (const f of Object.keys(fields)) {
    const v = fields[f][side];
    if (v === ABSENT) delete out[f];
    else out[f] = v;
  }
  return out;
}

export function emptyHlDiff(): HlDiff {
  return { removed: [], added: [], changed: [] };
}

export function isEmptyHlDiff(d: HlDiff): boolean {
  return !d.removed.length && !d.added.length && !d.changed.length;
}

export function diffHighlights(before: readonly Highlight[], after: readonly Highlight[]): HlDiff {
  const d = emptyHlDiff();
  if (before === after) return d;
  const bk = keysOf(before);
  const ak = keysOf(after);
  const aIdx = new Map(ak.map((k, i) => [k, i]));
  const bSet = new Set(bk);
  before.forEach((h, i) => {
    const j = aIdx.get(bk[i]);
    if (j === undefined) { d.removed.push({ key: bk[i], h }); return; }
    const a = after[j];
    if (a === h) return;
    const fields = fieldDiff(h, a);
    if (fields) d.changed.push({ key: bk[i], fields, before: h, after: a });
  });
  after.forEach((h, j) => { if (!bSet.has(ak[j])) d.added.push({ key: ak[j], h }); });
  return d;
}

// Fields the user owns on a moment — what an undo copies onto a moment that came back meanwhile.
const FLAG_FIELDS = ['isPinned', 'isDeleted', 'isPromoted'] as const;
const BOUND_FIELDS = ['clipStartTime', 'clipEndTime', 'isStartTimeManuallySet', 'estimatedSizeMb'] as const;
// Sources that keep startTime unique via uniqueStartTime — re-placed on re-insert collisions.
const UNIQUE_TIME_SOURCES = new Set(['user-clip', 'transcript-ai', 'transcript-search']);

/**
 * Apply a diff onto the CURRENT list (undo = inverse, redo = forward). Returns `current`
 * itself when nothing applied.
 *  1. remove what the step created (added on undo, removed on redo) — if still there;
 *  2. patch only the recorded fields of changed moments, so concurrent AI / rebuild changes to
 *     other fields survive. A changed peak a rebuild has dropped meanwhile is re-inserted only
 *     if the restored copy would survive a rebuild (pinned / merged) — otherwise its
 *     highlightStateCache entry brings it back with its cluster;
 *  3. bring back the other side's objects: absent → insert at its startTime (a clip / speech
 *     item re-placed if its time is taken); present (came back meanwhile) → on undo copy only
 *     the user's flags (+ clip bounds if they were set by hand), on redo replace with the
 *     stored object (a merged moment must not degrade).
 */
export function applyHighlightDiff(current: readonly Highlight[], d: HlDiff, dir: Dir): readonly Highlight[] {
  const undo = dir === 'undo';
  const side: 0 | 1 = undo ? 0 : 1;
  let list = current as Highlight[];
  let changed = false;

  // 1. Remove.
  const removeKeys = new Set((undo ? d.added : d.removed).map(x => x.key));
  if (removeKeys.size) {
    const keys = keysOf(list);
    const next = list.filter((_, i) => !removeKeys.has(keys[i]));
    if (next.length !== list.length) { list = next; changed = true; }
  }

  // 2. Patch changed moments.
  const reinsert: Highlight[] = [];
  if (d.changed.length) {
    const idx = indexByKey(list);
    let next: Highlight[] | null = null;
    for (const c of d.changed) {
      const i = idx.get(c.key);
      if (i === undefined) {
        const restored = undo ? c.before : c.after;
        if (restored.source === 'peak' && (restored.isPinned || restored.isMerged)) reinsert.push(restored);
        continue;
      }
      const cur = (next ?? list)[i];
      const patched = withFields(cur, c.fields, side);
      if (fieldDiff(cur, patched)) {
        next ??= list.slice();
        next[i] = patched;
      }
    }
    if (next) { list = next; changed = true; }
  }

  // 3. Bring back.
  const bring = [...reinsert.map(h => ({ key: '', h })), ...(undo ? d.removed : d.added)];
  if (bring.length) {
    const idx = indexByKey(list);
    const replace = new Map<Highlight, Highlight>();
    const inserts: Highlight[] = [];
    const taken = new Set(list.map(h => h.startTime));
    for (const { key, h } of bring) {
      const i = key ? idx.get(key) : idx.get(baseKey(h));
      if (i === undefined) {
        let item = h;
        if (UNIQUE_TIME_SOURCES.has(h.source ?? '') && taken.has(h.startTime)) {
          item = { ...h, startTime: uniqueStartTime(h.startTime, taken) };
        }
        taken.add(item.startTime);
        inserts.push(item);
        continue;
      }
      const cur = replace.get(list[i]) ?? list[i];
      let next: Highlight;
      if (undo) {
        const fields: (keyof Highlight)[] = [...FLAG_FIELDS];
        if (h.isStartTimeManuallySet) fields.push(...BOUND_FIELDS);
        const copy: any = { ...cur };
        let any = false;
        for (const f of fields) {
          const v = own(h, f) ? (h as any)[f] : ABSENT;
          const cv = own(cur, f) ? (cur as any)[f] : ABSENT;
          if (Object.is(v, cv)) continue;
          any = true;
          if (v === ABSENT) delete copy[f]; else copy[f] = v;
        }
        if (!any) continue;
        next = copy;
      } else {
        if (cur === h) continue;
        next = h;
      }
      replace.set(list[i], next);
    }
    if (replace.size) { list = list.map(h => replace.get(h) ?? h); changed = true; }
    if (inserts.length) {
      list = list.slice();
      for (const h of inserts) {
        let at = list.findIndex(x => x.startTime > h.startTime);
        if (at === -1) at = list.length;
        list.splice(at, 0, h);
      }
      changed = true;
    }
  }

  return changed ? list : current;
}

// --- highlightStateCache diff ---

export type CacheDiff<V> = Array<[number, V | undefined, V | undefined]>;

/** Entries whose value reference differs. Cache values are fresh literals on every write. */
export function diffCache<V>(before: ReadonlyMap<number, V>, after: ReadonlyMap<number, V>): CacheDiff<V> {
  const out: CacheDiff<V> = [];
  for (const [k, b] of before) {
    const a = after.get(k);
    if (a !== b) out.push([k, b, a]);
  }
  for (const [k, a] of after) {
    if (!before.has(k)) out.push([k, undefined, a]);
  }
  return out;
}

/** In place on the live Map (undefined = delete). Returns whether anything changed. */
export function applyCacheDiff<V>(cache: Map<number, V>, d: CacheDiff<V>, dir: Dir): boolean {
  let changed = false;
  for (const [k, b, a] of d) {
    const v = dir === 'undo' ? b : a;
    if (cache.get(k) === v && (v !== undefined || !cache.has(k))) continue;
    if (v === undefined) cache.delete(k);
    else cache.set(k, v);
    changed = true;
  }
  return changed;
}

// --- Scalars, merge selection ---

export type ScalarDiff = Record<string, [unknown, unknown]>;

export function regionEquals(a: TimeRegion | null | undefined, b: TimeRegion | null | undefined): boolean {
  if (!a || !b) return !a && !b;
  return a.inSec === b.inSec && a.outSec === b.outSec;
}

export function scalarEquals(name: string, a: unknown, b: unknown): boolean {
  if (name === 'regionRange') return regionEquals(a as TimeRegion | null, b as TimeRegion | null);
  return Object.is(a, b);
}

export function filtersEqual(a: ChatFilters, b: ChatFilters): boolean {
  const same = (x: string[], y: string[]) => x.length === y.length && x.every((v, i) => v === y[i]);
  return a.hideSubs === b.hideSubs && same(a.keywords, b.keywords) && same(a.ignoredUsers, b.ignoredUsers);
}

export interface SetDelta { added: number[]; removed: number[] }

export function setDelta(before: ReadonlySet<number>, after: ReadonlySet<number>): SetDelta | null {
  if (before === after) return null;
  const added = [...after].filter(x => !before.has(x));
  const removed = [...before].filter(x => !after.has(x));
  return added.length || removed.length ? { added, removed } : null;
}

/** Member-wise, so checkbox toggles made after the step survive. Always a new Set. */
export function applySetDelta(cur: ReadonlySet<number>, d: SetDelta, dir: Dir): Set<number> {
  const out = new Set(cur);
  const [drop, add] = dir === 'undo' ? [d.added, d.removed] : [d.removed, d.added];
  for (const x of drop) out.delete(x);
  for (const x of add) out.add(x);
  return out;
}

// --- Patch bundle ---

export interface PatchDiff {
  hl: HlDiff;
  cache: CacheDiff<unknown>;
  scalars: ScalarDiff;
  merge: SetDelta | null;
}

// Applied with an entry but never create one on their own (a search-history chip whose query
// fails only changes the input box; isAnalyzed is a derived flag).
export const RIDE_ALONG = new Set(['searchInputValue', 'isAnalyzed']);

export function isEmptyPatch(d: PatchDiff): boolean {
  return isEmptyHlDiff(d.hl) && !d.cache.length && Object.keys(d.scalars).every(k => RIDE_ALONG.has(k));
}

type Slot =
  | { t: 'removed'; h: Highlight }
  | { t: 'added'; h: Highlight }
  | { t: 'changed'; c: HlChange };

function slotsOf(d: HlDiff): Map<HlKey, Slot> {
  const m = new Map<HlKey, Slot>();
  for (const x of d.removed) m.set(x.key, { t: 'removed', h: x.h });
  for (const x of d.added) m.set(x.key, { t: 'added', h: x.h });
  for (const c of d.changed) m.set(c.key, { t: 'changed', c });
  return m;
}

function composeHl(d1: HlDiff, d2: HlDiff): HlDiff {
  const s1 = slotsOf(d1);
  const s2 = slotsOf(d2);
  const out = emptyHlDiff();
  const put = (key: HlKey, s: Slot | null) => {
    if (!s) return;
    if (s.t === 'removed') out.removed.push({ key, h: s.h });
    else if (s.t === 'added') out.added.push({ key, h: s.h });
    else out.changed.push(s.c);
  };
  for (const [key, a] of s1) {
    const b = s2.get(key);
    if (!b) { put(key, a); continue; }
    if (a.t === 'added') {
      if (b.t === 'removed') continue;                                  // created then dropped
      put(key, { t: 'added', h: b.t === 'changed' ? b.c.after : b.h });
    } else if (a.t === 'changed') {
      if (b.t === 'removed') { put(key, { t: 'removed', h: a.c.before }); continue; }
      const after = b.t === 'changed' ? b.c.after : b.h;
      const fields = fieldDiff(a.c.before, after);
      if (fields) put(key, { t: 'changed', c: { key, fields, before: a.c.before, after } });
    } else {                                                            // removed in d1
      if (b.t !== 'added') { put(key, a); continue; }
      const fields = fieldDiff(a.h, b.h);
      if (fields) put(key, { t: 'changed', c: { key, fields, before: a.h, after: b.h } });
    }
  }
  for (const [key, b] of s2) if (!s1.has(key)) put(key, b);
  return out;
}

/** Fold d2 into d1 (coalescing): first before + latest after; net no-ops disappear. */
export function composePatch(d1: PatchDiff, d2: PatchDiff): PatchDiff {
  const cache = new Map<number, [unknown, unknown]>();
  for (const [k, b, a] of d1.cache) cache.set(k, [b, a]);
  for (const [k, b, a] of d2.cache) {
    const prev = cache.get(k);
    cache.set(k, [prev ? prev[0] : b, a]);
  }
  const scalars: ScalarDiff = {};
  for (const name of new Set([...Object.keys(d1.scalars), ...Object.keys(d2.scalars)])) {
    const b = name in d1.scalars ? d1.scalars[name][0] : d2.scalars[name][0];
    const a = name in d2.scalars ? d2.scalars[name][1] : d1.scalars[name][1];
    if (!scalarEquals(name, a, b)) scalars[name] = [b, a];
  }
  let merge: SetDelta | null = d1.merge;
  if (d2.merge) {
    const added = new Set(d1.merge?.added ?? []);
    const removed = new Set(d1.merge?.removed ?? []);
    for (const x of d2.merge.added) { if (removed.has(x)) removed.delete(x); else added.add(x); }
    for (const x of d2.merge.removed) { if (added.has(x)) added.delete(x); else removed.add(x); }
    merge = added.size || removed.size ? { added: [...added], removed: [...removed] } : null;
  }
  return {
    hl: composeHl(d1.hl, d2.hl),
    cache: [...cache].filter(([, [b, a]]) => b !== a).map(([k, [b, a]]) => [k, b, a]),
    scalars,
    merge,
  };
}

// --- Result groups replaced by a newer AI / «Речь» run ---

export type ResultGroup = 'search' | 'transcript-ai' | 'transcript-search';

const SEARCH_SCALARS = ['searchQuery', 'searchBinMatches', 'searchExactMatch', 'searchInputValue'];

const inGroup = (h: Highlight, g: ResultGroup) =>
  g === 'search' ? h.source === 'search-keyword' || h.source === 'search-ai' : h.source === g;

// What the run itself replaces: mergeSearchHighlights keeps only promoted search results,
// mergeTranscriptHighlights keeps pinned / promoted / trashed items of its source and, for a run
// limited to a region, everything outside it.
const replacedBy = (h: Highlight, g: ResultGroup, scope: TimeRegion | null) =>
  inGroup(h, g)
  && (g === 'search' ? !h.isPromoted : !(h.isPinned || h.isPromoted || h.isDeleted))
  && (!scope || overlapsRegion(h.startTime, h.endTime, scope));

/** What a replacing run did: its region (null = whole stream) and the list before / after it. */
export interface GroupRun {
  scope: TimeRegion | null;
  before: readonly Highlight[];
  after: readonly Highlight[];
}

/**
 * A newer unrecorded run replaced group `g`: drop from a step what the run replaced, so undoing an
 * older search / clear never mixes stale results into the new ones and no step is left that
 * undoes nothing. Removed / added items go when the run would have replaced them; a changed
 * item goes when the run dropped it (or it was already gone and the run would have replaced it).
 * For the chat search group its scalars (query, heatmap tint) go too.
 */
export function purgeGroup(d: PatchDiff, g: ResultGroup, run: GroupRun): PatchDiff {
  const afterSet = new Set(run.after);
  const beforeKeys = keysOf(run.before);
  const dropped = new Set(beforeKeys.filter((_, i) => !afterSet.has(run.before[i])));
  const present = new Set(keysOf(run.after));
  const purged = new Set<number>();
  const kept = new Set<number>();
  const keep = (t: number, ok: boolean) => { (ok ? kept : purged).add(t); return ok; };
  const replaced = (...objs: Highlight[]) => objs.every(o => replacedBy(o, g, run.scope));
  const hl: HlDiff = {
    removed: d.hl.removed.filter(x => keep(x.h.startTime, !replaced(x.h))),
    added: d.hl.added.filter(x => keep(x.h.startTime, !replaced(x.h))),
    changed: d.hl.changed.filter(c => keep(c.before.startTime,
      !dropped.has(c.key) && (present.has(c.key) || !replaced(c.before, c.after)))),
  };
  const cache = d.cache.filter(([k]) => !purged.has(k) || kept.has(k));
  const scalars: ScalarDiff = {};
  for (const [name, v] of Object.entries(d.scalars)) {
    if (g === 'search' && SEARCH_SCALARS.includes(name)) continue;
    scalars[name] = v;
  }
  return { hl, cache, scalars, merge: d.merge };
}

// --- Selection, stack ---

/**
 * Index to select after an undo / redo: the first candidate key the current tab shows, else the
 * visible moment nearest `nearT`, else the current one if visible, else the first visible.
 * -1 when nothing is visible.
 */
export function pickSelection(
  list: readonly Highlight[],
  candidates: Array<HlKey | null>,
  isVisible: (h: Highlight) => boolean,
  nearT: number | null,
  currentIndex: number,
): number {
  if (!list.length) return -1;
  const idx = indexByKey(list);
  for (const c of candidates) {
    if (c == null) continue;
    const i = idx.get(c);
    if (i !== undefined && isVisible(list[i])) return i;
  }
  if (nearT != null) {
    let best = -1;
    let bestD = Infinity;
    list.forEach((h, i) => {
      if (!isVisible(h)) return;
      const dist = Math.abs(h.startTime - nearT);
      if (dist < bestD) { bestD = dist; best = i; }
    });
    if (best !== -1) return best;
  }
  if (currentIndex >= 0 && currentIndex < list.length && isVisible(list[currentIndex])) return currentIndex;
  return list.findIndex(isVisible);
}

export function pushCapped<T>(stack: T[], item: T, cap: number): void {
  stack.push(item);
  while (stack.length > cap) stack.shift();
}
